import { useNavigation } from '@react-navigation/native';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import Toast from 'react-native-root-toast';

import { POS_TYPE } from '../../../common/constants';
import { CREATE_MEDIA } from '../../../common/graphql/media';
import { getFullMachineName } from '../../../common/utils/machine';
import { useOfflineMutation, useOfflineQueue } from '../../offline';
import { filterRemoteUrls } from '../../offline/uploadAttachments';
import { useManualRestockFlow } from '../manualRestock';
import { getMyDayTrainingRouteParams, isMyDayTraining } from '../myDay/myDayTraining';

const getDocketName = docket => getFullMachineName(docket?.machine, docket?.machine?.place, docket?.machine?.location);

/**
 * Navigation to a stop's pages and the stop-level actions (manual restock,
 * quick photo), for any service docket: a stop of the driver's own day (via
 * useDriverDayActions) or a docket opened from another day or run (StopView).
 *
 * Params:
 * - docket: the stop the manual restock flow, quick photo and onStepPress act on
 * - planned: the run date (pick lists and manual restock need it); a target
 *   docket's own `serviceRun.planned` wins when present
 * - trainingContext: admin training context (route param `myDayTraining`), or null
 * - isTrainingTarget(docket): optional; limits training to some stops (the
 *   driver day passes "is one of the training run's stops")
 * - onChanged: called after a manual restock or quick photo is saved online (refetch)
 * - captureMedia / capturedMediaBatch: from withMediaPicker (collectCapturedMediaBatch),
 *   for openQuickPhoto and, when captureBeforeOpenPhotos is true, the Photos step
 * - captureBeforeOpenPhotos: prompt and save before opening the Photos step
 *
 * Every opener takes the target docket (default `docket`); openPickList, openPhotos
 * and openConfirmStockLevels also take extra route params (e.g. { nextStepLabel }).
 */
const useStopActions = ({
	docket,
	planned,
	trainingContext = null,
	isTrainingTarget,
	onChanged,
	captureMedia,
	capturedMediaBatch,
	captureBeforeOpenPhotos = false,
}) => {
	const navigation = useNavigation();
	const { isOnline } = useOfflineQueue();
	const isTraining = isMyDayTraining(trainingContext);
	const trainingRouteParams = useMemo(() => getMyDayTrainingRouteParams(trainingContext), [trainingContext]);
	const quickPhotoDocketRef = useRef(null);
	const lastQuickPhotoBatchIdRef = useRef(null);

	const requireTrainingConnection = useCallback(() => {
		if (isTraining && !isOnline) {
			Toast.show('Reconnect to continue training');
			return false;
		}

		return true;
	}, [isOnline, isTraining]);
	const isTargetTraining = useCallback(targetDocket => Boolean(isTraining &&
		(!isTrainingTarget || isTrainingTarget(targetDocket))), [isTraining, isTrainingTarget]);
	const getTargetTrainingParams = useCallback(targetDocket => (
		isTargetTraining(targetDocket) ? trainingRouteParams : {}
	), [isTargetTraining, trainingRouteParams]);
	const requireTargetConnection = useCallback(targetDocket => (
		!isTargetTraining(targetDocket) || requireTrainingConnection()
	), [isTargetTraining, requireTrainingConnection]);
	const isDocketTraining = isTargetTraining(docket);

	const manualRestockFlow = useManualRestockFlow({
		serviceDocketId: docket?.id,
		isRestocked: docket?.isRestocked,
		startedProcessing: docket?.startedProcessing,
		machine: docket?.machine,
		planned,
		onManualRestockSuccess: onChanged,
		disableOffline: isDocketTraining,
		adminTraining: isDocketTraining,
	});

	const openPickList = useCallback((targetDocket = docket, routeParams) => {
		// Stops loaded outside the driver day carry their own run (useStopDocket).
		const targetPlanned = targetDocket?.serviceRun?.planned || planned;
		if (!targetDocket?.pickList?.id || !targetPlanned || !requireTargetConnection(targetDocket)) {
			return;
		}

		navigation.push('PickListDetailView', {
			docketId: targetDocket.id,
			id: targetDocket.pickList.id,
			machineName: getDocketName(targetDocket),
			planned: targetPlanned,
			status: targetDocket.status,
			isAnyItemPicked: targetDocket.pickList.isAnyItemPicked,
			hasProductSoldSinceLastPickListRecalculation: targetDocket.pickList.hasProductSoldSinceLastPickListRecalculation,
			...routeParams,
			...getTargetTrainingParams(targetDocket),
		});
	}, [docket, navigation, planned, getTargetTrainingParams, requireTargetConnection]);

	const openConfirmStockLevels = useCallback((targetDocket = docket, routeParams) => {
		if (!targetDocket?.id || !requireTargetConnection(targetDocket)) {
			return;
		}

		navigation.push('ConfirmStockLevelsView', { docketId: targetDocket.id, ...routeParams, ...getTargetTrainingParams(targetDocket) });
	}, [docket, navigation, getTargetTrainingParams, requireTargetConnection]);

	const openConfirmValues = useCallback((targetDocket = docket) => {
		if (!targetDocket?.id || !requireTargetConnection(targetDocket)) {
			return;
		}

		navigation.push('ConfirmMachineValuesView', {
			docketId: targetDocket.id,
			isCoffee: Boolean(targetDocket?.machine?.usesIngredients),
			...getTargetTrainingParams(targetDocket),
		});
	}, [docket, navigation, getTargetTrainingParams, requireTargetConnection]);

	const openTickets = useCallback((targetDocket = docket) => {
		if (!targetDocket?.ticketList?.id || !targetDocket?.machine?.id || !requireTargetConnection(targetDocket)) {
			return;
		}

		navigation.push('TicketListDetailView', {
			id: targetDocket.ticketList.id,
			docketId: targetDocket.id,
			machineId: targetDocket.machine.id,
			machineName: getDocketName(targetDocket),
			...getTargetTrainingParams(targetDocket),
		});
	}, [docket, navigation, getTargetTrainingParams, requireTargetConnection]);

	const openPhotos = useCallback((targetDocket = docket, routeParams) => {
		if (!targetDocket?.id || !targetDocket?.machine?.id || !requireTargetConnection(targetDocket)) {
			return;
		}

		navigation.push('MediaListView', {
			docketId: targetDocket.id,
			machineId: targetDocket.machine.id,
			machineName: getDocketName(targetDocket),
			machineType: targetDocket.machine?.type,
			isMarket: targetDocket.machine?.type === POS_TYPE.MARKET,
			...routeParams,
			...getTargetTrainingParams(targetDocket),
		});
	}, [docket, navigation, getTargetTrainingParams, requireTargetConnection]);

	const openCreateTicket = useCallback((targetDocket = docket) => {
		if (!targetDocket?.id || !targetDocket?.machine?.id || !requireTargetConnection(targetDocket)) {
			return;
		}

		navigation.push('TicketCreator', {
			machineId: targetDocket.machine.id,
			machineName: getDocketName(targetDocket),
			docketId: targetDocket.id,
			...getTargetTrainingParams(targetDocket),
		});
	}, [docket, navigation, getTargetTrainingParams, requireTargetConnection]);

	const openQuickPhoto = useCallback((targetDocket = docket, photoRouteParams) => {
		if (!captureMedia || !targetDocket?.id || !targetDocket?.machine?.id || !requireTargetConnection(targetDocket)) {
			return;
		}

		quickPhotoDocketRef.current = { docket: targetDocket, photoRouteParams };
		captureMedia(photoRouteParams ? { quickMediaCapture: false, selectionLimit: 1 } : undefined);
	}, [captureMedia, docket, requireTargetConnection]);
	const [createQuickPhoto] = useOfflineMutation(CREATE_MEDIA, {
		configKey: 'createMedia',
		disableOffline: isDocketTraining,
	});

	// One batch per capture session (a market capture can hold several photos).
	useEffect(() => {
		const { docket: photoDocket, photoRouteParams } = quickPhotoDocketRef.current || {};
		if (
			!capturedMediaBatch ||
			capturedMediaBatch.id === lastQuickPhotoBatchIdRef.current ||
			!photoDocket?.id ||
			!photoDocket?.machine?.id
		) {
			return;
		}

		lastQuickPhotoBatchIdRef.current = capturedMediaBatch.id;
		quickPhotoDocketRef.current = null;

		// Training bypasses queue preparation, so only uploaded photos can be saved.
		if (isTargetTraining(photoDocket) && filterRemoteUrls(capturedMediaBatch.links).length !== capturedMediaBatch.links.length) {
			Toast.show('Photo not uploaded. Check your connection and take it again.');
			return;
		}

		Promise.all(capturedMediaBatch.links.map(link => createQuickPhoto({
			variables: {
				input: {
					machine: photoDocket.machine.id,
					serviceDocket: photoDocket.id,
					link,
					...(isTargetTraining(photoDocket) ? { adminTraining: true } : {}),
				},
			},
			context: {
				offlineMeta: {
					machineId: photoDocket.machine.id,
					machineName: getDocketName(photoDocket),
				},
			},
		}))).then((results) => {
			const isOffline = results.some(result => result?.offline);
			Toast.show(isOffline
				? 'Photo queued; will sync when back online'
				: 'Photo uploaded successfully');
			if (photoRouteParams) {
				openPhotos(photoDocket, photoRouteParams);
			}
			if (!isOffline && onChanged) {
				onChanged();
			}
		}).catch(error => Toast.show(error?.message || 'Unable to save the photo'));
	}, [capturedMediaBatch, createQuickPhoto, isTargetTraining, onChanged, openPhotos]);

	// Opens the page of a stop step (buildPerStopSteps ids). Returns false only
	// for an unknown step id so callers can fall back to their own handling.
	const onStepPress = useCallback((step, routeParams) => {
		if (!docket || step.blocked || !requireTargetConnection(docket)) {
			return true;
		}

		switch (step.id) {
			case 'pick':
				openPickList(docket);
				return true;
			case 'restock':
				manualRestockFlow.openManualRestockAlert();
				return true;
			case 'confirm_values':
				openConfirmValues(docket);
				return true;
			case 'confirm_stock':
				openConfirmStockLevels(docket, routeParams);
				return true;
			case 'tickets':
				openTickets(docket);
				return true;
			case 'photos':
				if (captureBeforeOpenPhotos) {
					openQuickPhoto(docket, routeParams || {});
				}
				else {
					openPhotos(docket, routeParams);
				}
				return true;
			default:
				return false;
		}
	}, [
		captureBeforeOpenPhotos,
		docket,
		manualRestockFlow,
		openConfirmStockLevels,
		openConfirmValues,
		openPhotos,
		openQuickPhoto,
		openPickList,
		openTickets,
		requireTargetConnection,
	]);

	return {
		isTraining: isDocketTraining,
		trainingRouteParams: getTargetTrainingParams(docket),
		requireTrainingConnection: () => requireTargetConnection(docket),
		openPickList,
		openConfirmStockLevels,
		openConfirmValues,
		openTickets,
		openPhotos,
		openCreateTicket,
		openQuickPhoto,
		onStepPress,
		manualRestock: {
			openAlert: manualRestockFlow.openManualRestockAlert,
			isOpen: manualRestockFlow.isManualRestockWarningOpen,
			setIsOpen: manualRestockFlow.setIsManualRestockWarningOpen,
			submit: manualRestockFlow.manualRestock,
			values: manualRestockFlow.values,
			changeFormValue: manualRestockFlow.changeFormValue,
		},
	};
};

export default useStopActions;
